import type { FC } from 'react';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import AkWarningIcon from '@atlaskit/icon/core/warning';
import AkSpinner from '@atlaskit/spinner/spinner';

import { ErrorDisplay } from '@confluence/error-boundary';
import type { ForgeUISpaceSettingsExtensionType } from '@confluence/forge-ui';
import { FORGE_MODULE_SPACE_SETTINGS, lazyForgeUIExtensionsWithLoader } from '@confluence/forge-ui';

export const ANALYTICS_SOURCE = 'forgeAppSpaceSettingsV2Navigation';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledErrorContainer = styled.div({
	alignSelf: 'flex-end',
	paddingBottom: token('space.025'),
	height: '20px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledErrorMessage = styled.span({
	font: token('font.body'),
	color: token('color.text.subtle'),
});

const i18n = defineMessages({
	errorLoadingApps: {
		id: 'space-settings.forge-apps-tabs.error-loading',
		defaultMessage: 'Some apps may have failed to load',
		description:
			'Message appearing in Forge app navigation warning that some apps failed to load, if there is an error',
	},
});

type ForgeAppTabsProps = {
	showLoadingErrorStates?: boolean;
	// Optional because it is only needed on fully React, SPA pages
	onLoadForgeApps?: (apps: readonly ForgeUISpaceSettingsExtensionType[]) => void;
	onError?: (error: Error) => void;
};

/**
 * Retrieves confluence:spaceSettings Forge apps and renders a tab menu item per app.
 * Once apps data is loaded, this component will send that data to the parent via onLoadForgeApps callback.
 * Props forgeAppId, forgeEnvId and forgeRoutePath determine which app appears selected in the tab group, if any.
 * The parent must render the selected app's contents.
 */
export const ForgeAppsLoader: FC<ForgeAppTabsProps> = ({
	showLoadingErrorStates = true,
	onLoadForgeApps,
	onError,
}) => {
	const LazyLoaderForgeUIExtensions = useMemo(
		() =>
			// display spinner while Forge extensions are lazy loading
			lazyForgeUIExtensionsWithLoader(() =>
				showLoadingErrorStates ? <AkSpinner size="small" /> : null,
			),
		[showLoadingErrorStates],
	);

	const render = useCallback(
		(apps: any, loading: any, error: any) => {
			return (
				<ForgeAppsLoaderComponent
					apps={apps as ReadonlyArray<ForgeUISpaceSettingsExtensionType>}
					loading={loading}
					error={error}
					onLoadForgeApps={onLoadForgeApps}
					onError={onError}
					showLoadingErrorStates={showLoadingErrorStates}
				/>
			);
		},
		[onLoadForgeApps, onError, showLoadingErrorStates],
	);

	return (
		<LazyLoaderForgeUIExtensions
			moduleType={FORGE_MODULE_SPACE_SETTINGS}
			render={render}
			returnBlockedExtensions
		/>
	);
};

type ForgeAppsLoaderComponentProps = ForgeAppTabsProps & {
	apps: ReadonlyArray<ForgeUISpaceSettingsExtensionType>;
	loading: boolean;
	error?: Error;
};

const ForgeAppsLoaderComponent: FC<ForgeAppsLoaderComponentProps> = ({
	apps,
	loading,
	error,
	onError,
	onLoadForgeApps,
	showLoadingErrorStates,
}) => {
	const [allApps, setAllApps] = useState<ReadonlyArray<ForgeUISpaceSettingsExtensionType>>([]);
	const [errorInState, setErrorInState] = useState<Error | undefined>(undefined);
	useEffect(() => {
		const a = apps.map((app) => app.id);
		const b = allApps.map((app) => app.id);
		// Set the apps into state, but only if the data is new.
		// The length check is quicker so execute first; but if lengths are equal, check actual app IDs.
		if (
			apps.length !== allApps.length ||
			// Only need to check one way if array length is the same (assume there are no duplicate IDs)
			!a.every((el) => b.includes(el))
		) {
			setAllApps(apps);
			onLoadForgeApps && onLoadForgeApps(apps);
		}
	}, [apps, allApps, onLoadForgeApps]);

	useEffect(() => {
		if (error && error.message !== errorInState?.message) {
			setErrorInState(error);
			onError && onError(error);
		}
	}, [error, onError, errorInState]);

	if (!showLoadingErrorStates) {
		return null;
	}

	if (loading) {
		return <AkSpinner size="small" testId="forge-apps-tabs-loading-spinner" />;
	}

	// this component handles loading/error states, but the actual tabs for the apps
	// themselves are rendered by a parent component due to the need to control
	// them alongside non-Forge tabs
	return error ? <ForgeAppLinksLoadingError error={error} /> : null;
};

export const ForgeAppLinksLoadingError: FC<{ error: Error }> = ({ error }) => {
	return (
		<ErrorDisplay error={error}>
			<StyledErrorContainer>
				<AkWarningIcon label="" color={token('color.icon.warning')} />
				<StyledErrorMessage>
					<FormattedMessage {...i18n.errorLoadingApps} />
				</StyledErrorMessage>
			</StyledErrorContainer>
		</ErrorDisplay>
	);
};
